import type {
  ICheckEmailResponse,
  IConfirmOTPDTO,
  IConfirmOTPResponse,
  ILoginDTO,
  ILoginResponse,
  IPasswordResetDTO,
  IPasswordResetResponse,
  IProfileResponse,
  IRegisterDTO,
  IRegisterResponse,
  IRequestOTPDTO,
  IRequestOTPResponse,
  IUpdateProfileDTO,
} from './types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { apiClient } from '@/shared/api'
import { isAuthenticated } from '@/shared/lib'

/**
 * Auth Api
 */

// === Check Email ===

// Check Email Request
export function checkEmailRequest(email: string) {
  return apiClient.post<ICheckEmailResponse>('/auth/check-email/', { email })
}

// Check Email Mutation
export function useCheckEmailMutation() {
  return useMutation({
    mutationFn: checkEmailRequest,
  })
}

// === Login ===

// Login Request
export function loginRequest(data: ILoginDTO) {
  return apiClient.post<ILoginResponse>('/token/', data)
}

// Login Mutation
export function useLoginMutation() {
  return useMutation({ mutationFn: loginRequest })
}

// --- Profile ---

// Profile Request
export function fetchProfileRequest() {
  return apiClient.get<IProfileResponse>('/auth/profile/')
}

// Profile Query
export function useFetchProfileQuery() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfileRequest,
    enabled: isAuthenticated(), // Only run when user is authenticated
  })
}

// Update Profile Request
export function updateProfileRequest(data: IUpdateProfileDTO) {
  return apiClient.patch<IProfileResponse>('/auth/update-profile/', data)
}

// Update Profile Mutation
export function useUpdateProfileMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateProfileRequest,
    onSuccess: () => {
      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}

// --- Telegram Disconnect ---

// Drop the user's Telegram connection on the backend (clears chat id + legacy username).
export function disconnectTelegramRequest() {
  return apiClient.post<IProfileResponse>('/auth/telegram/disconnect/')
}

export function useDisconnectTelegramMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: disconnectTelegramRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}

// === Register ===

// Register Request
export function registerRequest(data: IRegisterDTO) {
  return apiClient.post<IRegisterResponse>('/auth/register/', data)
}

// Register Mutation
export function useRegisterMutation() {
  return useMutation({
    mutationFn: registerRequest,
  })
}

// === Request OTP ===

// Request OTP Request
export function requestOTPRequest(data: IRequestOTPDTO) {
  return apiClient.post<IRequestOTPResponse>('/wb-accounts/request-otp/', data)
}

// Request OTP Mutation
export function useRequestOTPMutation() {
  return useMutation({
    mutationFn: requestOTPRequest,
  })
}

// === Confirm OTP ===

// Confirm OTP Request
export function confirmOTPRequest(data: IConfirmOTPDTO) {
  return apiClient.post<IConfirmOTPResponse>('/wb-accounts/confirm-otp/', data)
}

// Confirm OTP Mutation
export function useConfirmOTPMutation() {
  return useMutation({
    mutationFn: confirmOTPRequest,
  })
}

// === Password Reset ===

// Password Reset Request
export function passwordResetRequest(data: IPasswordResetDTO) {
  return apiClient.post<IPasswordResetResponse>('/auth/password-reset/', data)
}

// Password Reset Mutation
export function usePasswordResetMutation() {
  return useMutation({
    mutationFn: passwordResetRequest,
  })
}
